import React, { useMemo, useState } from 'react';
import {
  CheckCircle2,
  ClipboardList,
  Download,
  GitBranch,
  Play,
  Plus,
  Save,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  Users,
  Workflow,
} from 'lucide-react';
import toast from 'react-hot-toast';

type TriggerType = 'client_created' | 'visit_completed' | 'incident_logged' | 'medication_missed' | 'complaint_received';
type StepType = 'task' | 'form' | 'approval' | 'notification' | 'audit_tag';
type OwnerRole = 'Admin' | 'Care Coordinator' | 'Carer' | 'Registered Manager' | 'Family Liaison';
type WorkflowStatus = 'Draft' | 'Active' | 'Paused';

interface WorkflowStep {
  id: string;
  title: string;
  type: StepType;
  owner: OwnerRole;
  dueHours: number;
  required: boolean;
}

interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  trigger: TriggerType;
  status: WorkflowStatus;
  approvalRequired: boolean;
  evidenceTags: string[];
  steps: WorkflowStep[];
}

const triggerLabels: Record<TriggerType, string> = {
  client_created: 'Client created',
  visit_completed: 'Visit completed',
  incident_logged: 'Incident logged',
  medication_missed: 'Medication missed',
  complaint_received: 'Complaint received',
};

const stepLabels: Record<StepType, string> = {
  task: 'Task',
  form: 'Form',
  approval: 'Approval',
  notification: 'Notification',
  audit_tag: 'Audit tag',
};

const statusStyles: Record<WorkflowStatus, string> = {
  Draft: 'bg-slate-100 text-slate-700',
  Active: 'bg-emerald-100 text-emerald-800',
  Paused: 'bg-amber-100 text-amber-800',
};

const initialWorkflows: WorkflowTemplate[] = [
  {
    id: 'wf-client-intake',
    name: 'Client intake and first care plan',
    description: 'Starts when a new client is created and guides assessment, risk review and care plan approval.',
    trigger: 'client_created',
    status: 'Active',
    approvalRequired: true,
    evidenceTags: ['client intake', 'care plan', 'risk assessment'],
    steps: [
      { id: 'step-1', title: 'Complete initial needs assessment', type: 'form', owner: 'Care Coordinator', dueHours: 24, required: true },
      { id: 'step-2', title: 'Create draft care plan', type: 'task', owner: 'Care Coordinator', dueHours: 48, required: true },
      { id: 'step-3', title: 'Registered manager approval', type: 'approval', owner: 'Registered Manager', dueHours: 72, required: true },
      { id: 'step-4', title: 'Notify family contact', type: 'notification', owner: 'Family Liaison', dueHours: 72, required: false },
    ],
  },
  {
    id: 'wf-medication-missed',
    name: 'Missed medication escalation',
    description: 'Creates escalation tasks and audit evidence when a missed medication is recorded in eMAR.',
    trigger: 'medication_missed',
    status: 'Active',
    approvalRequired: true,
    evidenceTags: ['eMAR', 'medication safety', 'CQC Safe'],
    steps: [
      { id: 'step-5', title: 'Record reason for missed dose', type: 'form', owner: 'Carer', dueHours: 1, required: true },
      { id: 'step-6', title: 'Coordinator review and family update', type: 'task', owner: 'Care Coordinator', dueHours: 2, required: true },
      { id: 'step-7', title: 'Manager sign-off', type: 'approval', owner: 'Registered Manager', dueHours: 24, required: true },
      { id: 'step-8', title: 'Tag evidence for CQC medicines optimisation', type: 'audit_tag', owner: 'Admin', dueHours: 24, required: true },
    ],
  },
  {
    id: 'wf-complaints',
    name: 'Complaint response workflow',
    description: 'Tracks complaint acknowledgement, investigation, response and learning action.',
    trigger: 'complaint_received',
    status: 'Draft',
    approvalRequired: true,
    evidenceTags: ['Regulation 16', 'complaints', 'Responsive'],
    steps: [
      { id: 'step-9', title: 'Acknowledge complaint', type: 'notification', owner: 'Admin', dueHours: 48, required: true },
      { id: 'step-10', title: 'Investigate and record findings', type: 'form', owner: 'Registered Manager', dueHours: 120, required: true },
      { id: 'step-11', title: 'Create learning action', type: 'task', owner: 'Care Coordinator', dueHours: 168, required: true },
    ],
  },
];

const emptyStep: Omit<WorkflowStep, 'id'> = {
  title: '',
  type: 'task',
  owner: 'Care Coordinator',
  dueHours: 24,
  required: true,
};

export const WorkflowBuilder: React.FC = () => {
  const [workflows, setWorkflows] = useState(initialWorkflows);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(initialWorkflows[0]!.id);
  const [stepDraft, setStepDraft] = useState(emptyStep);
  const [simulationTrigger, setSimulationTrigger] = useState<TriggerType>('medication_missed');

  const selectedWorkflow = workflows.find((workflow) => workflow.id === selectedWorkflowId) ?? workflows[0]!;

  const activeCount = workflows.filter((workflow) => workflow.status === 'Active').length;
  const approvalCount = workflows.filter((workflow) => workflow.approvalRequired).length;
  const totalSteps = workflows.reduce((sum, workflow) => sum + workflow.steps.length, 0);

  const matchedSimulation = useMemo(
    () => workflows.filter((workflow) => workflow.trigger === simulationTrigger),
    [simulationTrigger, workflows]
  );

  const updateSelectedWorkflow = (updates: Partial<WorkflowTemplate>) => {
    setWorkflows((items) =>
      items.map((workflow) =>
        workflow.id === selectedWorkflow.id ? { ...workflow, ...updates } : workflow
      )
    );
  };

  const addWorkflow = () => {
    const workflow: WorkflowTemplate = {
      id: `wf-${Date.now()}`,
      name: 'New custom workflow',
      description: 'Configure trigger, steps, owners and approval rules.',
      trigger: 'visit_completed',
      status: 'Draft',
      approvalRequired: false,
      evidenceTags: ['custom'],
      steps: [
        { id: `step-${Date.now()}`, title: 'Review workflow event', type: 'task', owner: 'Admin', dueHours: 24, required: true },
      ],
    };
    setWorkflows((items) => [workflow, ...items]);
    setSelectedWorkflowId(workflow.id);
    toast.success('Workflow template created');
  };

  const addStep = () => {
    if (!stepDraft.title.trim()) {
      toast.error('Add a step title first');
      return;
    }

    const nextStep: WorkflowStep = {
      ...stepDraft,
      id: `step-${Date.now()}`,
      dueHours: Math.max(1, Number(stepDraft.dueHours) || 1),
    };

    updateSelectedWorkflow({
      steps: [...selectedWorkflow.steps, nextStep],
    });
    setStepDraft(emptyStep);
    toast.success('Workflow step added');
  };

  const removeStep = (stepId: string) => {
    updateSelectedWorkflow({
      steps: selectedWorkflow.steps.filter((step) => step.id !== stepId),
    });
    toast.success('Workflow step removed');
  };

  const publishWorkflow = () => {
    updateSelectedWorkflow({ status: selectedWorkflow.status === 'Active' ? 'Paused' : 'Active' });
    toast.success(selectedWorkflow.status === 'Active' ? 'Workflow paused' : 'Workflow activated');
  };

  const exportWorkflow = () => {
    const blob = new Blob([JSON.stringify(selectedWorkflow, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedWorkflow.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Workflow configuration exported');
  };

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
              <Workflow className="h-7 w-7" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
                Workflow Builder
              </p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">Custom Workflows</h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
                Configure operational flows with triggers, required steps, assigned owners,
                approval rules, evidence tags and exportable templates.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={addWorkflow}
              className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Plus className="h-4 w-4" />
              New Workflow
            </button>
            <button
              type="button"
              onClick={exportWorkflow}
              className="inline-flex h-10 items-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white hover:bg-[#5e3474]"
            >
              <Download className="h-4 w-4" />
              Export JSON
            </button>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-4">
        <MetricCard label="Templates" value={String(workflows.length)} icon={<GitBranch className="h-5 w-5" />} />
        <MetricCard label="Active" value={String(activeCount)} icon={<CheckCircle2 className="h-5 w-5" />} tone="success" />
        <MetricCard label="Approval rules" value={String(approvalCount)} icon={<ShieldCheck className="h-5 w-5" />} />
        <MetricCard label="Configured steps" value={String(totalSteps)} icon={<ClipboardList className="h-5 w-5" />} />
      </section>

      <section className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <div className="space-y-4">
          {workflows.map((workflow) => (
            <button
              key={workflow.id}
              type="button"
              onClick={() => setSelectedWorkflowId(workflow.id)}
              className={`w-full rounded-lg border bg-white p-5 text-left shadow-sm transition hover:border-[#6c3a80]/40 ${
                selectedWorkflow.id === workflow.id ? 'border-[#6c3a80] ring-2 ring-[#6c3a80]/10' : 'border-slate-200'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-bold text-slate-950">{workflow.name}</h2>
                  <p className="mt-1 text-sm leading-6 text-slate-600">{workflow.description}</p>
                </div>
                <span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusStyles[workflow.status]}`}>
                  {workflow.status}
                </span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold text-slate-600">
                <span className="rounded-full bg-slate-100 px-2 py-1">
                  Trigger: {triggerLabels[workflow.trigger]}
                </span>
                <span className="rounded-full bg-slate-100 px-2 py-1">
                  Steps: {workflow.steps.length}
                </span>
                {workflow.approvalRequired && (
                  <span className="rounded-full bg-purple-100 px-2 py-1 text-purple-800">
                    Approval required
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>

        <div className="space-y-6">
          <article className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <Settings2 className="h-5 w-5 text-[#6c3a80]" />
                  <h2 className="text-xl font-bold text-slate-950">Workflow Configuration</h2>
                </div>
                <p className="mt-2 text-sm text-slate-600">
                  Changes are kept in this browser session until connected to the production database.
                </p>
              </div>
              <button
                type="button"
                onClick={publishWorkflow}
                className="inline-flex h-10 items-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white hover:bg-[#5e3474]"
              >
                <Save className="h-4 w-4" />
                {selectedWorkflow.status === 'Active' ? 'Pause' : 'Activate'}
              </button>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <Field label="Workflow name">
                <input
                  value={selectedWorkflow.name}
                  onChange={(event) => updateSelectedWorkflow({ name: event.target.value })}
                  className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                />
              </Field>
              <Field label="Trigger">
                <select
                  value={selectedWorkflow.trigger}
                  onChange={(event) => updateSelectedWorkflow({ trigger: event.target.value as TriggerType })}
                  className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                >
                  {Object.entries(triggerLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </Field>
              <div className="md:col-span-2">
                <Field label="Description">
                  <textarea
                    value={selectedWorkflow.description}
                    onChange={(event) => updateSelectedWorkflow({ description: event.target.value })}
                    rows={3}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  />
                </Field>
              </div>
              <Field label="Evidence tags">
                <input
                  value={selectedWorkflow.evidenceTags.join(', ')}
                  onChange={(event) => updateSelectedWorkflow({
                    evidenceTags: event.target.value.split(',').map((tag) => tag.trim()).filter(Boolean),
                  })}
                  className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                />
              </Field>
              <label className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={selectedWorkflow.approvalRequired}
                  onChange={(event) => updateSelectedWorkflow({ approvalRequired: event.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-[#6c3a80] focus:ring-[#6c3a80]"
                />
                Manager approval required before completion
              </label>
            </div>
          </article>

          <article className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <SlidersHorizontal className="h-5 w-5 text-[#6c3a80]" />
              <h2 className="text-xl font-bold text-slate-950">Workflow Steps</h2>
            </div>

            <div className="mt-5 space-y-3">
              {selectedWorkflow.steps.map((step, index) => (
                <div key={step.id} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#6c3a80] text-sm font-bold text-white">
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-950">{step.title}</h3>
                        <p className="mt-1 text-sm text-slate-600">
                          {stepLabels[step.type]} | {step.owner} | due in {step.dueHours}h
                          {step.required ? ' | required' : ' | optional'}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeStep(step.id)}
                      className="rounded-md p-2 text-slate-500 hover:bg-white hover:text-red-600"
                      aria-label={`Remove ${step.title}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 rounded-lg border border-slate-200 bg-white p-4">
              <h3 className="font-semibold text-slate-950">Add Step</h3>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Field label="Step title">
                  <input
                    value={stepDraft.title}
                    onChange={(event) => setStepDraft((current) => ({ ...current, title: event.target.value }))}
                    className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                    placeholder="Example: Manager sign-off"
                  />
                </Field>
                <Field label="Step type">
                  <select
                    value={stepDraft.type}
                    onChange={(event) => setStepDraft((current) => ({ ...current, type: event.target.value as StepType }))}
                    className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                  >
                    {Object.entries(stepLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Owner">
                  <select
                    value={stepDraft.owner}
                    onChange={(event) => setStepDraft((current) => ({ ...current, owner: event.target.value as OwnerRole }))}
                    className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                  >
                    {['Admin', 'Care Coordinator', 'Carer', 'Registered Manager', 'Family Liaison'].map((owner) => (
                      <option key={owner} value={owner}>{owner}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Due hours">
                  <input
                    type="number"
                    min={1}
                    value={stepDraft.dueHours}
                    onChange={(event) => setStepDraft((current) => ({ ...current, dueHours: Number(event.target.value) }))}
                    className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                  />
                </Field>
                <label className="flex items-center gap-3 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={stepDraft.required}
                    onChange={(event) => setStepDraft((current) => ({ ...current, required: event.target.checked }))}
                    className="h-4 w-4 rounded border-slate-300 text-[#6c3a80] focus:ring-[#6c3a80]"
                  />
                  Required step
                </label>
              </div>
              <button
                type="button"
                onClick={addStep}
                className="mt-4 inline-flex h-10 items-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white hover:bg-[#5e3474]"
              >
                <Plus className="h-4 w-4" />
                Add Step
              </button>
            </div>
          </article>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <Play className="h-5 w-5 text-[#6c3a80]" />
              <h2 className="text-xl font-bold text-slate-950">Trigger Simulation</h2>
            </div>
            <p className="mt-2 text-sm text-slate-600">
              Preview which workflows will run when an event occurs.
            </p>
          </div>
          <select
            value={simulationTrigger}
            onChange={(event) => setSimulationTrigger(event.target.value as TriggerType)}
            className="h-10 rounded-md border border-slate-300 px-3 text-sm"
          >
            {Object.entries(triggerLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        <div className="mt-5 grid gap-4 lg:grid-cols-3">
          {matchedSimulation.length > 0 ? (
            matchedSimulation.map((workflow) => (
              <article key={workflow.id} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-slate-950">{workflow.name}</h3>
                    <p className="mt-1 text-sm text-slate-600">{workflow.steps.length} steps will be created.</p>
                  </div>
                  <span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusStyles[workflow.status]}`}>
                    {workflow.status}
                  </span>
                </div>
                <div className="mt-3 flex items-center gap-2 text-sm text-slate-600">
                  <Users className="h-4 w-4 text-[#6c3a80]" />
                  Next owner: {workflow.steps[0]?.owner ?? 'Unassigned'}
                </div>
              </article>
            ))
          ) : (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              No workflow currently matches this trigger.
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

function MetricCard({
  label,
  value,
  icon,
  tone = 'default',
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  tone?: 'default' | 'success';
}) {
  return (
    <article className={`rounded-lg border p-4 shadow-sm ${tone === 'success' ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
        <div className="text-[#6c3a80]">{icon}</div>
      </div>
      <p className="mt-2 text-3xl font-bold text-slate-950">{value}</p>
    </article>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-800">{label}</span>
      {children}
    </label>
  );
}
